/**
 * PlaylistSearchTileV6 — the picked combination.
 *
 *   Hero  = Golden Spiral Cascade         — same V5-style fanned-deck
 *                                          treatment, but the cards
 *                                          trace a true golden spiral
 *                                          (logarithmic spiral with the
 *                                          golden ratio φ as growth
 *                                          factor per quarter-turn).
 *                                          Front card centered; each
 *                                          subsequent card sits 36°
 *                                          further around the spiral
 *                                          with the radius multiplied
 *                                          by φ^(θ / (π/2)). All static
 *                                          — scale + opacity decay
 *                                          independently with index so
 *                                          the trail fades to nothing
 *                                          regardless of where the
 *                                          spiral position lands.
 *   Hover = V6 hover body                 — V1's layout, infinity
 *                                          carousel running INSIDE the
 *                                          hover panel's strip (36 s
 *                                          cycle, ~4 s center hold,
 *                                          smaller cards for spacing).
 *
 * Shares the 280 px shell + faded gradient backdrop + meta block + hover
 * panel chrome with the rest of the variation set via PlaylistTileShell.
 */

import React from 'react'
import StackedCards from '../../../../Icon/icons/StackedCards/index'
import type { PlaylistSearchTileProps } from '../../shared/types'
import PlaylistTileShell from '../../shared/PlaylistTileShell'
import PlaylistTileHoverBodyV6 from './PlaylistTileHoverBodyV6'

// ── Golden-spiral parameters (logarithmic) ────────────────────────────
// The classic golden / nautilus spiral. Each subsequent card is rotated
// by a small constant angle and placed at radius
//
//     r(θ) = R₀ × φ^(θ / (π/2))
//
// — i.e. the radius multiplies by φ over every quarter-turn, the
// defining property of the golden spiral. Combined with a small
// angular step, the cards trace one continuous curve from the center
// outward (rather than scattering opposite-side-each-step like the
// phyllotaxis pattern, which is also "golden" but visually different).
const PHI = (1 + Math.sqrt(5)) / 2
// Angular step per card. 22.5° → 16 cards per full revolution → the
// curve reads as smooth + continuous instead of discrete jumps.
const STEP_ANGLE_RAD = Math.PI / 8
// Radius at the first trailing card (i = 1, θ = 0). Sized so the
// inner curl (i = 1–3) tucks just behind / beside the front card and
// the visible arc (i = 4–10) emerges above and to the left. The front
// card extends ~55 px from deck center, so 40 px keeps i = 1–3 partly
// occluded (the "tightening into the core" feel of a nautilus shell)
// while letting i = 4 onward sweep clearly above the front card.
const STARTING_RADIUS_PX = 40
// Vertical offset applied to *every* card (front + trailing). Pushes
// the whole deck a bit below the hero's mid-line so the spiral arc has
// room above without losing meta-block headroom below.
const DECK_VERTICAL_OFFSET_PX = 16
// Angular offset applied to the trailing-spiral start. With 0 rad,
// i = 1 sits directly to the right of the front card; π/8 rad (22.5°)
// raises it to a subtle upper-right so the spiral arc reads as
// "rising" without lifting it too far above the front card edge.
const SPIRAL_START_OFFSET_RAD = Math.PI / 8
// Per-index decay. Front card is fully opaque; the first trailing card
// (i = 1) already starts visibly faded at TRAILING_OPACITY_START, then
// each subsequent card drops by OPACITY_DECAY_PER_INDEX. Scale decay
// starts at 1.0 with the front card and tracks index.
const SCALE_DECAY_PER_INDEX = 0.05
const OPACITY_DECAY_PER_INDEX = 0.07
const TRAILING_OPACITY_START = 0.82
const MIN_SCALE = 0.28
const MIN_OPACITY = 0.05
// Subtle counter-clockwise lean per card so the deck flows along the
// spiral rather than reading as a set of static disconnected pieces.
const ROTATION_PER_INDEX_DEG = -1.0

interface SpiralWaypoint {
  /** Translate-X in pixels, relative to the deck's centered anchor. */
  dx: number
  /** Translate-Y in pixels (negative = up). */
  dy: number
  /** Rotation in degrees. */
  rot: number
  /** Scale multiplier. */
  sc: number
  /** Opacity. */
  op: number
}

/**
 * Returns the logarithmic golden-spiral waypoint for card `i`. Card 0
 * sits at the centered peak (the front of the deck) at full size; each
 * subsequent card is rotated by `STEP_ANGLE_RAD` and placed at radius
 *
 *     r(θ) = R₀ × φ^(θ / (π/2))
 *
 * — the canonical golden-spiral law (radius × φ per quarter-turn).
 * The spiral starts at θ = 0 for i = 1 (so the first trailing card sits
 * directly right of the front card) and sweeps counter-clockwise.
 */
function goldenSpiralWaypoint (i: number): SpiralWaypoint {
  if (i === 0) {
    return {
      dx: 0, dy: DECK_VERTICAL_OFFSET_PX, rot: 0, sc: 1.0, op: 1.0,
    }
  }
  // Trailing spiral starts at SPIRAL_START_OFFSET_RAD so i = 1 sits
  // upper-right of the front card (raising the second card relative
  // to a straight-right θ = 0 start).
  const theta = (i - 1) * STEP_ANGLE_RAD + SPIRAL_START_OFFSET_RAD
  const r = STARTING_RADIUS_PX * (PHI ** (theta / (Math.PI / 2)))
  // Negate dy so positive sin(θ) translates to "up" on screen — the
  // spiral reads counter-clockwise, matching the conventional
  // nautilus / golden-spiral orientation in the user's reference.
  const dx = r * Math.cos(theta)
  const dy = -r * Math.sin(theta) + DECK_VERTICAL_OFFSET_PX
  const sc = Math.max(MIN_SCALE, 1 - SCALE_DECAY_PER_INDEX * i)
  // First trailing card starts at TRAILING_OPACITY_START (visibly
  // faded vs the front card's 1.0); subsequent cards step down by
  // OPACITY_DECAY_PER_INDEX.
  const op = Math.max(
    MIN_OPACITY,
    TRAILING_OPACITY_START - OPACITY_DECAY_PER_INDEX * (i - 1),
  )
  const rot = ROTATION_PER_INDEX_DEG * i
  return {
    dx, dy, rot, sc, op,
  }
}

interface SpiralCascadeProps {
  thumbnails: string[]
  hoverActive: boolean
}

function SpiralCascade ({ thumbnails, hoverActive }: SpiralCascadeProps): React.ReactElement {
  if (thumbnails.length === 0) {
    return (
      <div className="playlist-search-tile-v6__placeholder">
        <StackedCards size="40px" color="rgba(255, 255, 255, 0.65)" />
      </div>
    )
  }

  if (thumbnails.length === 1) {
    return (
      <div className="playlist-search-tile-v6__stage">
        <div className="playlist-search-tile-v6__deck">
          <div className="playlist-search-tile-v6__card playlist-search-tile-v6__card--single">
            <img
              src={thumbnails[0]}
              alt=""
              loading="lazy"
              className="playlist-search-tile-v6__img"
            />
          </div>
        </div>
      </div>
    )
  }

  const stageClassName = hoverActive
    ? 'playlist-search-tile-v6__stage playlist-search-tile-v6__stage--hover-active'
    : 'playlist-search-tile-v6__stage'

  return (
    <div className={stageClassName}>
      <div className="playlist-search-tile-v6__deck">
        {thumbnails.map((url, i) => {
          const p = goldenSpiralWaypoint(i)
          return (
            <div
              key={`${url}-${i}`}
              className="playlist-search-tile-v6__card"
              style={{
                transform: `translate(${p.dx}px, ${p.dy}px) rotate(${p.rot}deg) scale(${p.sc})`,
                opacity: p.op,
                zIndex: 100 - i,
                animationDelay: `${-i * 3}s`,
              }}
            >
              <img
                src={url}
                alt=""
                loading="lazy"
                className="playlist-search-tile-v6__img"
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function PlaylistSearchTileV6 (props: PlaylistSearchTileProps): React.ReactElement {
  return (
    <PlaylistTileShell
      {...props}
      renderHero={({ previewThumbnailUrls, hoverActive }) => (
        <SpiralCascade thumbnails={previewThumbnailUrls} hoverActive={hoverActive} />
      )}
      renderHoverBody={({
        playlistId,
        playlistName,
        metaLine,
        previewThumbnailUrls,
        previewContentTitles,
        itemCount,
      }) => (
        <PlaylistTileHoverBodyV6
          playlistId={playlistId}
          playlistName={playlistName}
          metaLine={metaLine}
          thumbnails={previewThumbnailUrls}
          titles={previewContentTitles}
          itemCount={itemCount}
        />
      )}
    />
  )
}

export default React.memo(PlaylistSearchTileV6)
